import getCookie from "./getCookie";
import setCookie from "./setCookie";

// P1D-5512 — room details side panel experiment.
// `section_name` says where a lead CTA was clicked; `room_details_variant` says which arm the
// user is in. Both travel from the CTA through the lead form and the bookform so a lead or a
// bookform can be traced back to room details (requirement §8).

export const ROOM_DETAILS_SECTION = {
  SIDE_PANEL: "room_details_side_panel",
  MODAL: "room_details_modal",
  ROOM_CARD: "room_card",
} as const;

export const ROOM_DETAILS_VARIANT = {
  MODAL: "modal",
  SIDE_PANEL: "side_panel",
} as const;

export type RoomDetailsSection = (typeof ROOM_DETAILS_SECTION)[keyof typeof ROOM_DETAILS_SECTION];
export type RoomDetailsVariant = (typeof ROOM_DETAILS_VARIANT)[keyof typeof ROOM_DETAILS_VARIANT];

export const ROOM_DETAILS_VARIANT_COOKIE = "room_details_variant";

/** The side panel needs this much window; below it the modal is used. */
export const SIDE_PANEL_MIN_VIEWPORT_WIDTH = 1200;
/** Query param on the bookform URL, next to `bookform_source`. */
const SECTION_NAME_PARAM = "section_name";

const isRoomDetailsSection = (value: unknown): value is RoomDetailsSection =>
  typeof value === "string" && (Object.values(ROOM_DETAILS_SECTION) as string[]).includes(value);

/** The arm the user was exposed to, or "" when they were never exposed (or no cookie access). */
export const getRoomDetailsVariant = (): RoomDetailsVariant | "" => {
  const value = getCookie(ROOM_DETAILS_VARIANT_COOKIE);
  return value && (Object.values(ROOM_DETAILS_VARIANT) as string[]).includes(value)
    ? (value as RoomDetailsVariant)
    : "";
};

export const setRoomDetailsVariant = (variant: RoomDetailsVariant) => {
  if (typeof window === "undefined") return;
  setCookie({ cname: ROOM_DETAILS_VARIANT_COOKIE, cvalue: variant });
};

/** `section_name` from the current URL (bookform), "" when absent or not a known value. */
export const getSectionNameFromUrl = (): RoomDetailsSection | "" => {
  if (typeof window === "undefined") return "";
  const value = new URLSearchParams(window.location.search).get(SECTION_NAME_PARAM);
  return isRoomDetailsSection(value) ? value : "";
};

/**
 * The two attribution properties for an event. A property is omitted — never sent empty — when
 * unknown, so events from ordinary PDP CTAs are unchanged.
 */
export const getRoomDetailsEventProps = (sectionName?: string) => {
  const variant = getRoomDetailsVariant();
  return {
    ...(sectionName && variant ? { section_name: sectionName } : {}),
    ...(variant ? { room_details_variant: variant } : {}),
  };
};

/**
 * Attribution for lead events: the section / arm, plus which room (and tenancy) the CTA belonged
 * to. Only when the CTA came from room details (`inventory.room_details_section`). A tenancy
 * object carries its room as `config`; a room-level CTA (Contact Us) is the room itself.
 */
export const getRoomDetailsLeadEventProps = (inventory: any) => {
  const section = inventory?.room_details_section;
  if (!section) return getRoomDetailsEventProps();
  return {
    ...getRoomDetailsEventProps(section),
    room_type_id: inventory?.config?.id ?? inventory?.id,
    ...(inventory?.config?.id ? { tenancy_id: inventory?.id } : {}),
  };
};
